import { useState } from "react";
import { Share2, Download, Copy, Check, Send } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import useMyRefCode from "@/hooks/useMyRefCode";
import { toast } from "sonner";

/** Public viral share tools — auto-generated card + one-click X/Telegram intents. */
export default function ShareTools({ coin }) {
  const [copied, setCopied] = useState(false);
  const refCode = useMyRefCode();
  const cardUrl = `${BACKEND_URL}/api/coins/${coin.slug}/card.png`;
  // /api/s/... serves OG/Twitter meta for link unfurls, then bounces humans to /c/...
  // Signed-in sharers auto-carry their referral code — every share recruits for them.
  const shareUrl = `${BACKEND_URL}/api/s/${coin.slug}${refCode ? `?ref=${refCode}` : ""}`;
  const text = `${coin.name} ($${coin.ticker}) just launched on SolSpur 🚀 Real SPL token on Solana.`;
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
  const tgUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`;

  const streakPing = () => {
    // Signed-in sharers grow their daily streak; anonymous users silently no-op (401).
    api.post("/me/share-ping").then((r) => {
      if (r.data.counted) toast.success(`Share streak: ${r.data.current_streak} day${r.data.current_streak === 1 ? "" : "s"} 🔥`);
    }).catch(() => {});
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      streakPing();
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — long-press the URL to copy manually");
    }
  };

  return (
    <section className="border border-[#3a3a3a] bg-[#121212] p-6 mb-6" data-testid="share-tools">
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#cfff04] mb-1">
        // Spread the word
      </div>
      <h3 className="font-display text-xl font-bold uppercase mb-4">Share ${coin.ticker}</h3>

      <div className="grid grid-cols-12 gap-4 items-start">
        <div className="col-span-12 md:col-span-6">
          <img
            src={cardUrl}
            alt={`${coin.name} share card`}
            data-testid="share-card-preview"
            className="w-full border border-[#3a3a3a]"
            loading="lazy"
          />
        </div>
        <div className="col-span-12 md:col-span-6 flex flex-col gap-2">
          <a
            href={xUrl}
            target="_blank"
            rel="noreferrer"
            onClick={streakPing}
            data-testid="share-x"
            className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-xs px-4 py-3 inline-flex items-center justify-center gap-2"
          >
            <Share2 size={13} /> Share on X
          </a>
          <a
            href={tgUrl}
            target="_blank"
            rel="noreferrer"
            onClick={streakPing}
            data-testid="share-telegram"
            className="border border-[#3a3a3a] hover:border-[#cfff04] hover:text-[#cfff04] text-[#e2e2e2] px-4 py-3 font-mono text-[11px] uppercase tracking-widest inline-flex items-center justify-center gap-2"
          >
            <Send size={12} /> Telegram
          </a>
          <a
            href={cardUrl}
            download={`${coin.slug}-card.png`}
            data-testid="share-download-card"
            className="border border-[#3a3a3a] hover:border-[#cfff04] hover:text-[#cfff04] text-[#e2e2e2] px-4 py-3 font-mono text-[11px] uppercase tracking-widest inline-flex items-center justify-center gap-2"
          >
            <Download size={12} /> Download card
          </a>
          <button
            onClick={copyLink}
            data-testid="share-copy-link"
            className="border border-[#3a3a3a] hover:border-[#cfff04] hover:text-[#cfff04] text-[#e2e2e2] px-4 py-3 font-mono text-[11px] uppercase tracking-widest inline-flex items-center justify-center gap-2"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      </div>
    </section>
  );
}
